import React, { useState } from "react";
import { Check, Info, ArrowLeft, ArrowDown, X } from "react-feather";
import { Button, Card, CardBody, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import growth from "../../../../assets/images/subscription/growth.png";
import enterprise from "../../../../assets/images/subscription/enterprise.png";
import fresh from "../../../../assets/images/subscription/fresh.png";
import supplier from "../../../../assets/images/subscription/supplier.png";
import group from "../../../../assets/images/subscription/group.png";
import FeatureTable from "./featureTable";
import { IApplicationState } from "../../../../redux/reducers";
import { connect } from "react-redux";
import { IUser } from "../../../../interfaces/user";
import StripeClimate from "../../../../components/checkout/stripeClimate";
import SubscriptionCard from "../../../subscribe/components/subscriptionCard";
import { availableSubscriptionPlans, SUBSCRIPTION_OPTIONS } from "../../../../utils/constants";
import { toCamelCase } from "../../../../utils/string";
import SubscriptionCheckoutModal from "../../../../components/checkout/subscriptionModal";
import { SparkleIcon } from "../../../../assets/icons";

interface IProps {
	profile: IUser | undefined;
}

const SubscriptionOptions: React.FC<IProps> = (props) => {
	const [selectedPlan, setSelectedPlan] = useState<availableSubscriptionPlans>();
	const [modal, setModal] = useState<boolean>();

	const handleSelectPlan = (plan: availableSubscriptionPlans) => {
		setSelectedPlan(plan);
		setModal(true);
	};

	return (
		<div className="subscription-options">
			<h3 className={"mb-3"}>
				<span style={{ color: "#3f65f1" }}>Pricing</span>
			</h3>

			{/* Start Free Trial Section */}
			<div className="start-free-section">
				<div className="start-free-content">
					<div className="start-free-left">
						<div className="start-free-header">
							<div className="start-free-icon-circle">
								<SparkleIcon size={24} color="#10b981" />
							</div>
							<div className="start-free-text">
								<h4>Start Free</h4>
								<p className="no-credit-card">No credit card required</p>
							</div>
						</div>
						<p className="trial-description">
							Try Journey Foods risk-free for 14 days. Get access to core features with
							limited credits to explore the platform.
						</p>
						<button className="btn-start-trial">
							<SparkleIcon size={16} color="#ffffff" />
							Start Free Trial
						</button>
					</div>
					<div className="start-free-right">
						<h5>Free trial includes:</h5>
						<div className="trial-features">
							<div className="trial-feature-col">
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 50 ingredient searches
								</div>
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 20 AI recommendations
								</div>
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 500 API calls
								</div>
							</div>
							<div className="trial-feature-col">
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 10 nutrition analyses
								</div>
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 3 report exports
								</div>
								<div className="trial-feature-item">
									<span className="checkmark">✓</span> 14-day access
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Pricing Cards */}
			<div className="pricing-cards-container">
				{Object.keys(SUBSCRIPTION_OPTIONS).map((key) => {
					const plan = SUBSCRIPTION_OPTIONS[key];
					return (
						<SubscriptionCard
							key={key}
							subscriptionName={plan.subscriptionName}
							subscriptionDetails={plan.subscriptionDetails}
							subscriptionPrice={plan.subscriptionPrice}
							subscriptionInterval={plan.subscriptionInterval}
							subscriptionDescription={plan.subscriptionDescription}
							dividerColor={plan.dividerColor}
							handleSelectPlan={handleSelectPlan}
							isPopular={plan.subscriptionName === availableSubscriptionPlans.growth}
							isSelected={selectedPlan === plan.subscriptionName}
						/>
					);
				})}
			</div>

			{/* How Credits Work Section */}
			<div className="credits-section">
				<h3>How Credits Work</h3>
				<p className="credits-description">
					Each plan includes monthly credits for different platform features. Credits reset monthly and scale with
					your plan. Need more? Upgrade anytime or purchase add-ons.
				</p>
				<div className="credits-table-container">
					<div className="credits-table">
						<div className="credits-column">
							<h5>Ingredient Searches</h5>
							<div className="credit-row">
								<span className="tier-label">Fresh</span>
								<span className="credit-value">50</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Growth</span>
								<span className="credit-value">200</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Group</span>
								<span className="credit-value">500</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Enterprise</span>
								<span className="credit-value unlimited">Unlimited</span>
							</div>
						</div>
						<div className="credits-column">
							<h5>Nutrition Analyses</h5>
							<div className="credit-row">
								<span className="tier-label">Fresh</span>
								<span className="credit-value">10</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Growth</span>
								<span className="credit-value">50</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Group</span>
								<span className="credit-value">100</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Enterprise</span>
								<span className="credit-value unlimited">Unlimited</span>
							</div>
						</div>
						<div className="credits-column">
							<h5>AI Recommendations</h5>
							<div className="credit-row">
								<span className="tier-label">Fresh</span>
								<span className="credit-value">20</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Growth</span>
								<span className="credit-value">100</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Group</span>
								<span className="credit-value">250</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Enterprise</span>
								<span className="credit-value unlimited">Unlimited</span>
							</div>
						</div>
						<div className="credits-column">
							<h5>Report Exports</h5>
							<div className="credit-row">
								<span className="tier-label">Fresh</span>
								<span className="credit-value">3</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Growth</span>
								<span className="credit-value">10</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Group</span>
								<span className="credit-value">25</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Enterprise</span>
								<span className="credit-value unlimited">Unlimited</span>
							</div>
						</div>
						<div className="credits-column">
							<h5>API Calls</h5>
							<div className="credit-row">
								<span className="tier-label">Fresh</span>
								<span className="credit-value">500</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Growth</span>
								<span className="credit-value">2000</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Group</span>
								<span className="credit-value">5,000</span>
							</div>
							<div className="credit-row">
								<span className="tier-label">Enterprise</span>
								<span className="credit-value unlimited">Unlimited</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className={"d-flex justify-content-center mt-5 mb-5"}>
				<div className="view-features">
					<a href="#features">View All Features</a>
					<ArrowDown />
				</div>
			</div>
			<Row id="features" className={"w-100"} style={{ display: "flex", flexDirection: "column" }}>
				<h3 style={{ alignSelf: "center", margin: "0.5rem" }}>All Features</h3>
				<FeatureTable />
			</Row>

			<Row
				className={"w-100 mt-3"}
				id="supplier-features"
				style={{ padding: "0 100px", display: "flex", flexDirection: "column" }}
			>
				<h3 className={"mb-3"} style={{ alignSelf: "center", margin: "0.5rem" }}>
					Supplier Pricing and Features
				</h3>
				<FeatureTable supplier />
			</Row>
			<Row className={"d-flex align-item-center justify-content-center pb-3"}>
				<div style={{ width: 500 }}>
					<StripeClimate />
				</div>
			</Row>
			{selectedPlan && (
				<Modal className="subscription-modal" isOpen={modal}>
					<ModalHeader>
						<div className={"modal-header-content"}>
							<span>{`Subscribe to ${toCamelCase(selectedPlan)} plan`}</span>
							<X
								onClick={() => {
									setModal(false);
								}}
								className={"close-modal"}
								size={24}
							></X>
						</div>
					</ModalHeader>
					<ModalBody>
						<SubscriptionCheckoutModal subscriptionPlan={selectedPlan} />
					</ModalBody>
				</Modal>
			)}
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	profile: state.user.profile
});

export default connect(mapStateToProps)(SubscriptionOptions);
